import { motion } from 'framer-motion';

import { Images } from '../../../constants/images';
import { SVGIconType } from '../../../constants/svgIcon';
import { SvgIcon } from '../../Molecules';

import styles from './GameBlankAnswerCardsGroup.module.css';

export enum AnswerCardStatus {
  Pending = 'Pending',
  Correct = 'Correct',
  Wrong = 'Wrong',
  HintEliminate = 'HintEliminate',
  Empty = 'Empty',
}

export type AnswerCard = {
  value: string;
  status: AnswerCardStatus;
};

type GameBlankAnswerCardsGroupProps = {
  cards: AnswerCard[];
  onCardClick: (card: AnswerCard) => void;
  onUpdateCardStatus: (card: AnswerCard, newStatus: AnswerCardStatus) => void;
  tutorialCard?: AnswerCard;
};

export const GameBlankAnswerCardsGroup = ({
  cards,
  onCardClick,
  onUpdateCardStatus,
  tutorialCard,
}: GameBlankAnswerCardsGroupProps) => {
  const hasLongValue = cards.some((card) => card.value.length > 1);

  return (
    <div
      className={
        hasLongValue ? styles.wrappingCardContainer : styles.cardContainer
      }
    >
      {cards.map((card, index) =>
        card.status === AnswerCardStatus.Empty ? (
          <div key={index} className={styles.emptyCardPlaceholder} />
        ) : card.status === AnswerCardStatus.HintEliminate ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.eliminateCard}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Empty);
              }, 1200); // Wait before updating status
            }}
          >
            {card.value}
            <SvgIcon name={SVGIconType.RedCross} className={styles.redCross} />
          </motion.div>
        ) : card.status === AnswerCardStatus.Correct ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.correctCard}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Empty);
              }, 600); // Wait before updating status
            }}
          >
            {card.value}
            {card === tutorialCard && (
              <motion.img
                src={Images.HandwriteFinger}
                alt="Finger pointing"
                className={styles.fingerOverlay}
                initial={{ x: 10, y: 10 }}
                animate={{ x: [8, -8], y: [8, -8] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
                style={{ pointerEvents: 'none' }}
              />
            )}
          </motion.div>
        ) : card.status === AnswerCardStatus.Wrong ? (
          <motion.div
            key={index}
            className={`${styles.card} ${styles.wrongCard}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            onAnimationComplete={() => {
              setTimeout(() => {
                onUpdateCardStatus(card, AnswerCardStatus.Pending);
              }, 600); // Wait before updating status
            }}
          >
            {card.value}
          </motion.div>
        ) : (
          <motion.div
            key={index}
            className={`${styles.card} ${
              card === tutorialCard ? styles.cardWithOverlay : ''
            }`}
            onClick={() => onCardClick(card)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.1, delay: index * 0.05 }}
          >
            {card.value}
            {card === tutorialCard &&
              card.status === AnswerCardStatus.Pending && (
                <motion.img
                  src={Images.HandwriteFinger}
                  alt="Finger pointing"
                  className={styles.fingerOverlay}
                  initial={{ x: 10, y: 10 }}
                  animate={{ x: [4, -4], y: [4, -4] }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    repeatType: 'reverse',
                  }}
                  style={{ pointerEvents: 'none' }}
                />
              )}
          </motion.div>
        ),
      )}
    </div>
  );
};
